import React, { memo, useMemo } from 'react';
import { ArrowForwardIos } from '@mui/icons-material';
import { Typography } from '@mui/material';

import { AssistantCommitAction } from '../../../data-access';
import './AssistantChatCommitActions.scss';
import { useTranslation } from 'react-i18next';
import type { DataTFunction } from '../../DataView/i18n';

interface AssistantChatCommitActionsProps {
  actions?: AssistantCommitAction[];
  onOpenSettings?: () => void;
}

const formatIntentType = (t: DataTFunction, intentType?: string) => {
  if (!intentType) {
    return t('task.ai.commitActions.action');
  }

  return t(`task.ai.commitActions.intentTypes.${intentType}`, {
    defaultValue: intentType.replace(/_/g, ' '),
  });
};

const AssistantChatCommitActions: React.FC<AssistantChatCommitActionsProps> = memo(
  ({ actions, onOpenSettings }) => {
    const { t } = useTranslation();
    const successfulActions = useMemo(
      () => (actions ?? []).filter((action) => action.status.toLowerCase() !== 'failed'),
      [actions]
    );

    if (!successfulActions.length) {
      return null;
    }

    return (
      <div className="assistant-chat-commit-actions">
        {successfulActions.map((action) => (
          <div key={action.action_id} className="assistant-chat-commit-actions__row-container">
            <div className="assistant-chat-commit-actions__row">
              <Typography className="assistant-chat-commit-actions__action-text" variant="body2">
                ✅ {t('task.ai.commitActions.updated')} {formatIntentType(t, action.intent_type)}
              </Typography>
              <Typography className="assistant-chat-commit-actions__summary" variant="body2">
                {action.summary}
              </Typography>
            </div>

            {action.intent_type === 'preference' ? (
              <button
                type="button"
                className="assistant-chat-commit-actions__settings-button"
                onClick={onOpenSettings}
                disabled={!onOpenSettings}
                aria-label={t('task.sidebar.openAiAssistantSettings')}
              >
                <ArrowForwardIos className="assistant-chat-commit-actions__settings-icon" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
);

AssistantChatCommitActions.displayName = 'AssistantChatCommitActions';

export default AssistantChatCommitActions;
